import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { authAPI, ticketAPI, analyticsAPI } from '@/lib/api';
import { POLLING_INTERVALS } from '@/lib/constants';
import { LogOut, AlertCircle, TrendingUp, DollarSign, Users, MessageSquare, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import SLATimer from '@/components/SLATimer';
import InterfaceSwitcher from '@/components/InterfaceSwitcher';
import ReportDownloadPanel from '@/components/ReportDownloadPanel';
import StatusDrilldownModal from '@/components/StatusDrilldownModal';
import FeedbackAnalyticsSection from '@/components/FeedbackAnalyticsSection';
import AdvancedTicketGraphs from '@/components/AdvancedTicketGraphs';
import axios from 'axios';

// Colour-coded configuration for the Ticket Status Distribution row on
// the CAO War Room. Order matches the product spec.
const STATUS_TILE_CONFIG = [
  { key: 'Open', bg: 'bg-sky-50', border: 'border-sky-200', labelColor: 'text-sky-700', countColor: 'text-sky-900', tooltip: 'Newly created tickets that have not been acknowledged yet.' },
  { key: 'Pending', bg: 'bg-yellow-50', border: 'border-yellow-300', labelColor: 'text-yellow-800', countColor: 'text-yellow-900', tooltip: 'Open tickets older than 12 hours with no resolver action — needs attention.' },
  { key: 'Acknowledged', bg: 'bg-indigo-50', border: 'border-indigo-200', labelColor: 'text-indigo-700', countColor: 'text-indigo-900', tooltip: 'Seen by the resolver but work has not started yet.' },
  { key: 'In Progress', bg: 'bg-amber-50', border: 'border-amber-200', labelColor: 'text-amber-700', countColor: 'text-amber-900', tooltip: 'Resolver actively working on the ticket.' },
  { key: 'Resolved', bg: 'bg-emerald-50', border: 'border-emerald-200', labelColor: 'text-emerald-700', countColor: 'text-emerald-900', tooltip: 'Work completed. Awaiting feedback or closed.' },
  { key: 'SLA BREACHED', bg: 'bg-red-50', border: 'border-red-300', labelColor: 'text-red-700', countColor: 'text-red-900', tooltip: 'Ticket crossed its Resolution TAT. Escalations active.' },
  { key: 'On Leave', bg: 'bg-cyan-50', border: 'border-cyan-200', labelColor: 'text-cyan-700', countColor: 'text-cyan-900', tooltip: 'Tickets currently being handled by a fill-in resolver (original resolver on leave).' },
  { key: 'Shift Completed', bg: 'bg-purple-50', border: 'border-purple-200', labelColor: 'text-purple-700', countColor: 'text-purple-900', tooltip: 'Handover to next-shift staff was done.' },
  { key: 'Purchase Material Requested', bg: 'bg-orange-50', border: 'border-orange-200', labelColor: 'text-orange-700', countColor: 'text-orange-900', tooltip: 'Material requisition raised to Stores/Accounts.' },
  { key: 'Purchase Material Approved', bg: 'bg-lime-50', border: 'border-lime-200', labelColor: 'text-lime-700', countColor: 'text-lime-900', tooltip: 'Stores/Accounts approved the material requisition.' },
  { key: 'Purchase Material Received', bg: 'bg-teal-50', border: 'border-teal-200', labelColor: 'text-teal-700', countColor: 'text-teal-900', tooltip: 'Material received — work can resume.' },
  { key: 'Ticket Diverted', bg: 'bg-fuchsia-50', border: 'border-fuchsia-200', labelColor: 'text-fuchsia-700', countColor: 'text-fuchsia-900', tooltip: 'Ticket rerouted to a different department.' },
];

const CAODashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [loadError, setLoadError] = useState(null);
  // Drilldown modal — opens when an admin clicks a status tile
  const [drilldownStatus, setDrilldownStatus] = useState(null);
  // Department list for the embedded Feedback Analytics section filters
  const [departmentList, setDepartmentList] = useState([]);

  const loadData = useCallback(async () => {
    // Promise.allSettled keeps each sibling independent — if the analytics
    // aggregation times out (common at cold-start), the user + tickets
    // fetches still populate, so the InterfaceSwitcher + ticket list
    // continue to render. This directly fixes the "empty switcher + no
    // interface buttons" flash the CAO War Room header was showing when
    // any single endpoint slowed down.
    const [userSettled, analyticsSettled, ticketsSettled] = await Promise.allSettled([
      authAPI.getMe(),
      analyticsAPI.getOverview(),
      ticketAPI.getTickets(),
    ]);

    let anyFailure = false;

    if (userSettled.status === 'fulfilled') {
      setUser(userSettled.value.data);
    } else {
      anyFailure = true;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('War Room /auth/me failed:', userSettled.reason?.message);
      }
    }

    if (analyticsSettled.status === 'fulfilled') {
      setAnalytics(analyticsSettled.value.data);
      const depts = (analyticsSettled.value.data?.department_stats || [])
        .map((d) => d.department)
        .filter(Boolean);
      setDepartmentList(depts);
    } else {
      anyFailure = true;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('War Room /analytics/overview failed:', analyticsSettled.reason?.message);
      }
    }

    if (ticketsSettled.status === 'fulfilled') {
      setTickets(ticketsSettled.value.data);
    } else {
      anyFailure = true;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('War Room /tickets failed:', ticketsSettled.reason?.message);
      }
    }

    setLoadError(
      anyFailure ? 'Live data refresh failed. Retrying in background…' : null,
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, POLLING_INTERVALS.ANALYTICS_REFRESH);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handlePingOwner = async (ticketId) => {
    // Email escalation happens automatically when SLA is breached
    toast.info('Email escalation sent automatically when SLA breached');
  };

  const handleGenerateReport = async () => {
    try {
      toast.info('Generating monthly report... You will receive it via email.');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/generate-report`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const breachedTickets = tickets.filter(t => t.status === 'SLA BREACHED');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-heading font-bold truncate">CAO War Room</h1>
              <p className="text-xs sm:text-sm text-slate-300 hidden sm:block">Real-time facility command center</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <InterfaceSwitcher user={user} currentInterface="cao" />
              <Button 
                data-testid="generate-report-btn"
                variant="outline" 
                onClick={handleGenerateReport} 
                size="sm"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 hidden sm:flex"
              >
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden md:inline">Generate Report</span>
              </Button>
              <Button 
                data-testid="manage-users-btn"
                variant="outline" 
                onClick={() => navigate('/users')} 
                size="sm"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 hidden sm:flex"
              >
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden md:inline">Manage Users</span>
              </Button>
              <Button 
                data-testid="logout-btn"
                variant="ghost" 
                onClick={handleLogout} 
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
          {/* Mobile-only action row */}
          <div className="flex gap-2 mt-2 sm:hidden">
            <Button 
              variant="outline" 
              onClick={handleGenerateReport} 
              size="sm"
              className="flex-1 bg-white/10 text-white border-white/20 hover:bg-white/20 text-xs"
            >
              <BarChart3 className="w-3 h-3 mr-1" />
              Report
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/users')} 
              size="sm"
              className="flex-1 bg-white/10 text-white border-white/20 hover:bg-white/20 text-xs"
            >
              <Users className="w-3 h-3 mr-1" />
              Users
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {loadError && (
          <div
            data-testid="warroom-load-error"
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800"
          >
            {loadError}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card data-testid="kpi-total" className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium opacity-90">Total Tickets</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{analytics?.total_tickets || 0}</p>
                </div>
                <BarChart3 className="w-8 h-8 sm:w-12 sm:h-12 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-open" className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium opacity-90">Pending &amp; Open</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{analytics?.open_tickets || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 sm:w-12 sm:h-12 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-breached" className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium opacity-90">SLA Breached</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{analytics?.breached_tickets || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 sm:w-12 sm:h-12 opacity-70 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="kpi-resolved" className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium opacity-90">Resolved</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{analytics?.resolved_tickets || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 sm:w-12 sm:h-12 opacity-50" />
              </div>
            </CardContent>
          </Card>

          {/* Full Status Distribution — one tile per tracked status */}
          <Card data-testid="status-distribution" className="col-span-full">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base">Ticket Status Distribution</CardTitle>
              <p className="text-xs text-slate-500">
                Click a tile to drill into the underlying tickets. &quot;Pending&quot; counts Open tickets that have not moved in over 12 hours; &quot;On Leave&quot; reflects tickets currently handled by a fill-in resolver.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {STATUS_TILE_CONFIG.map((cfg) => {
                  const count = analytics?.status_counts?.[cfg.key] || 0;
                  return (
                    <button
                      key={cfg.key}
                      type="button"
                      data-testid={`status-tile-${cfg.key.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setDrilldownStatus(cfg.key)}
                      className={`text-left rounded-lg border px-3 py-2 ${cfg.bg} ${cfg.border} hover:ring-2 hover:ring-[#E97832]/60 hover:scale-[1.02] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E97832]`}
                      title={`${cfg.tooltip} Click to inspect tickets.`}
                    >
                      <p className={`text-xs font-medium ${cfg.labelColor}`}>{cfg.key}</p>
                      <p className={`text-xl font-bold ${cfg.countColor}`}>{count}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Feedback Analytics — visible to every admin-panel member */}
          <Card data-testid="war-room-feedback-analytics" className="col-span-full bg-slate-900 border-slate-700 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#E97832]" />
                Feedback Analytics
              </CardTitle>
              <p className="text-xs text-slate-400">
                Every submitted feedback — creator, department, resolver, satisfaction and remark. Use the filters to slice by date / department / satisfaction.
              </p>
            </CardHeader>
            <CardContent>
              <FeedbackAnalyticsSection departments={departmentList} />
            </CardContent>
          </Card>

          <Card data-testid="war-room-widget" className="col-span-full md:col-span-2 row-span-2 border-2 border-red-200 bg-red-50/50">
            <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 font-heading">
                <AlertCircle className="w-5 h-5 animate-pulse" />
                War Room - SLA BREACHED Tickets
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {breachedTickets.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p className="text-lg font-medium">All Clear! 🎉</p>
                    <p className="text-sm mt-1">No SLA breaches at the moment</p>
                  </div>
                ) : (
                  breachedTickets.map((ticket) => (
                    <div 
                      key={ticket.ticket_id} 
                      data-testid={`breached-ticket-${ticket.ticket_id}`}
                      className="bg-white border-2 border-red-300 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-mono font-semibold text-sm text-red-700">{ticket.ticket_id}</p>
                          <p className="text-xs text-slate-600 mt-1">{ticket.department}</p>
                        </div>
                        <Badge variant="destructive" className="animate-pulse">BREACHED</Badge>
                      </div>
                      <p className="text-sm text-slate-700 mb-3 line-clamp-2">{ticket.description}</p>
                      <div className="flex items-center gap-2">
                        <Button 
                          data-testid={`ping-owner-${ticket.ticket_id}`}
                          size="sm" 
                          onClick={() => handlePingOwner(ticket.ticket_id)}
                          className="bg-[#E97832] hover:bg-[#C25E20] text-white"
                        >
                          <MessageSquare className="w-3 h-3 mr-1" />
                          Escalation Info
                        </Button>
                        <Button 
                          data-testid={`view-ticket-${ticket.ticket_id}`}
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="advanced-graphs-card" className="col-span-full md:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading">Advanced Ticket Analytics</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Auto-generated charts replacing the older pie-chart view. Each refresh runs a 20-second deliberate analysis window to give MongoDB time to aggregate and the UI a clear, predictable waiting state.
              </p>
            </CardHeader>
            <CardContent>
              <AdvancedTicketGraphs />
            </CardContent>
          </Card>

          {/* Report Download Panel */}
          <div className="col-span-full md:col-span-2">
            <ReportDownloadPanel user={user} />
          </div>
        </div>
      </main>

      {/* Status Distribution drilldown modal */}
      <StatusDrilldownModal
        status={drilldownStatus}
        open={!!drilldownStatus}
        onClose={() => setDrilldownStatus(null)}
      />
    </div>
  );
};

export default CAODashboard;
